import { defineNuxtPlugin } from '#app'
import type { TooltipBinding, TooltipOptions } from '@/types/plugins'
import type { DirectiveBinding, ObjectDirective } from 'vue'
import { nextTick } from 'vue'

// Extend HTMLElement to include a _tooltipCleanup property for removing listeners/observers on unmount
interface TargetElement extends HTMLElement {
	_tooltipCleanup?: () => void
}

// Default CSS classes for tooltip styling and transitions
const DEFAULT_CLASSES = 'bg-mx-gray-50 text-black dark:text-mx-gray-300 dark:bg-mx-green-900 p-2 rounded-md shadow-lg dark:shadow-white/5 text-sm'
const VISIBILITY_CLASSES = 'opacity-0 transition-opacity'

// Default offset values (in pixels) for positioning the tooltip relative to the trigger element
const DEFAULT_OFFSET = {
	x: 0,
	y: 4
}

// Default delay (in milliseconds) for showing/hiding the tooltip
const DEFAULT_DELAY = {
	show: 0,
	hide: 0
}

export default defineNuxtPlugin(nuxtApp => {
	// Define the tooltip directive
	const vTooltip: ObjectDirective = {
		async beforeMount(el: TargetElement, binding: DirectiveBinding<TooltipBinding>) {
			// Wait for the next DOM update cycle
			await nextTick()

			// Retrieve the binding value, which can be a string (tooltip text) or a full TooltipOptions object
			const raw = binding.value
			if (!raw) {
				return
			}

			// Normalize options
			let options: TooltipOptions
			if (typeof raw === 'string') {
				options = { content: raw }
			} else {
				options = raw
			}

			// Merge in default offset and delay values if not provided
			const offset = {
				...DEFAULT_OFFSET,
				...options.offset
			}
			const delay = {
				...DEFAULT_DELAY,
				...options.delay
			}
			// Set the initial alignment based on binding option; default is 'auto' (which is centered)
			const initialAlignment = options.placement || 'auto'

			// Create the tooltip element and set ARIA attributes for accessibility
			const tooltipEl = document.createElement('div')
			tooltipEl.setAttribute('role', 'tooltip')
			tooltipEl.setAttribute('aria-hidden', 'true')
			// Prevent any interaction on hidden tooltip
			tooltipEl.inert = true

			// Change cursor style to indicate interactivity
			el.style.cursor = 'pointer'

			// Set tooltip content: either as a text string or by appending an HTMLElement
			if (typeof options.content === 'string') {
				tooltipEl.innerText = options.content
			} else if (options.content instanceof HTMLElement) {
				tooltipEl.appendChild(options.content)
			} else {
				return
			}

			// Determine CSS classes: start hidden (display:none) and non-interactive
			const providedClasses = options.classes || ''
			const shouldOverrideStyles = options.overrideStyles && providedClasses.length > 0
			// Initial hidden state: hidden display + no pointer events
			const initialClasses = `${VISIBILITY_CLASSES} hidden pointer-events-none ${DEFAULT_CLASSES} ${providedClasses}`
			const tooltipClasses = shouldOverrideStyles ? initialClasses : `${initialClasses} ${providedClasses}`
			tooltipEl.className = tooltipClasses

			// Apply base inline styles for absolute positioning; start behind all elements
			Object.assign(tooltipEl.style, {
				position: 'absolute',
				whiteSpace: 'normal',
				top: '0',
				left: '0',
				zIndex: '-1'
			})

			// Function to update the tooltip's position based on the trigger element and viewport
			const updateTooltipPosition = () => {
				if (!tooltipEl.isConnected) {
					return
				}
				const rect = el.getBoundingClientRect()
				// Determine effective placement: 'auto' defaults to 'bottom'
				const placement = initialAlignment === 'auto' ? 'bottom' : initialAlignment
				// Calculate vertical position: below for 'bottom', above for 'top'
				let tooltipTop: number
				if (placement === 'top') {
					tooltipTop = rect.top + window.scrollY - tooltipEl.offsetHeight - offset.y
				} else {
					// 'bottom' or any other (including 'auto')
					tooltipTop = rect.bottom + window.scrollY + offset.y
				}

				// Get viewport boundaries
				const viewportLeft = window.scrollX
				const viewportRight = window.scrollX + window.innerWidth

				// Calculate horizontal positions: left, right, and center of element
				const tooltipWidth = tooltipEl.offsetWidth
				const leftPos = rect.left + window.scrollX - tooltipWidth - offset.x
				const rightPos = rect.right + window.scrollX + offset.x
				const centerPos = rect.left + window.scrollX + (rect.width - tooltipWidth) / 2 + offset.x

				let chosenLeft: number
				let textAlign: 'left' | 'right' | 'center'

				// Determine horizontal alignment
				if (initialAlignment === 'left') {
					chosenLeft = leftPos
					textAlign = 'right'
					// flip to right if overflowing
					if (chosenLeft < viewportLeft) {
						chosenLeft = rightPos
						textAlign = 'left'
					}
				} else if (initialAlignment === 'right') {
					chosenLeft = rightPos
					textAlign = 'left'
					// flip to left if overflowing
					if (chosenLeft + tooltipWidth > viewportRight) {
						chosenLeft = leftPos
						textAlign = 'right'
					}
				} else {
					// center alignment for 'top', 'bottom', or 'auto'
					chosenLeft = centerPos
					textAlign = 'center'
					// adjust if center overflows viewport
					if (chosenLeft < viewportLeft) {
						chosenLeft = viewportLeft
					} else if (chosenLeft + tooltipWidth > viewportRight) {
						chosenLeft = viewportRight - tooltipWidth
					}
				}

				// Apply computed positions and text alignment
				tooltipEl.style.left = `${chosenLeft}px`
				tooltipEl.style.top = `${tooltipTop}px`
				tooltipEl.style.textAlign = textAlign
			}

			// Track timeouts so we can cancel pending show/hide
			let showTimeout: number | undefined
			let hideTimeout: number | undefined
			const showTooltip = () => {
				// Cancel any pending hide
				if (hideTimeout) {
					clearTimeout(hideTimeout)
				}
				showTimeout = window.setTimeout(() => {
					if (!tooltipEl.isConnected) {
						document.body.appendChild(tooltipEl)
					}
					tooltipEl.inert = false
					tooltipEl.classList.add('pointer-events-auto', 'block', 'opacity-100')
					tooltipEl.classList.remove('hidden', 'opacity-0')
					tooltipEl.style.zIndex = '9999'
					// Update position now that element is in the DOM
					requestAnimationFrame(() => updateTooltipPosition())
				}, delay.show)
			}

			const hideTooltip = () => {
				// Cancel any pending show
				if (showTimeout) {
					clearTimeout(showTimeout)
				}
				// Immediately hide and disable interaction
				tooltipEl.classList.remove('block', 'opacity-100')
				tooltipEl.classList.add('hidden', 'pointer-events-none', 'opacity-0')
				tooltipEl.inert = true
				tooltipEl.style.zIndex = '-1'
				// Schedule removal from DOM after delay
				hideTimeout = window.setTimeout(() => {
					if (tooltipEl.isConnected) {
						tooltipEl.remove()
					}
				}, delay.hide)
			}

			const toggleTooltip = () => {
				if (tooltipEl.classList.contains('opacity-0') || tooltipEl.classList.contains('hidden')) {
					showTooltip()
				} else {
					hideTooltip()
				}
			}

			// Set the tooltip's initial position
			updateTooltipPosition()

			// Update tooltip position when the viewport changes
			window.addEventListener('scroll', updateTooltipPosition, true)
			window.addEventListener('resize', updateTooltipPosition, true)

			// Observe attribute changes on the trigger element to reposition the tooltip if needed
			const observer = new MutationObserver(() => {
				requestAnimationFrame(() => updateTooltipPosition())
			})
			observer.observe(el, { attributes: true })

			// Hide the tooltip when clicking outside of the trigger element or tooltip
			const onDocumentClick = (e: MouseEvent) => {
				if (!tooltipEl.contains(e.target as Node) && !el.contains(e.target as Node)) {
					hideTooltip()
				}
			}
			document.addEventListener('click', onDocumentClick, true)

			// Attach event listeners based on the specified trigger type
			if (options.trigger === 'click') {
				el.addEventListener('click', toggleTooltip)
			} else {
				el.addEventListener('mouseenter', showTooltip)
				el.addEventListener('mouseleave', hideTooltip)
			}

			// Store a cleanup function on the element to remove event listeners and observers on unmount
			el._tooltipCleanup = () => {
				window.removeEventListener('scroll', updateTooltipPosition, true)
				window.removeEventListener('resize', updateTooltipPosition, true)
				document.removeEventListener('click', onDocumentClick, true)
				observer.disconnect()

				if (options.trigger === 'click') {
					el.removeEventListener('click', toggleTooltip)
				} else {
					el.removeEventListener('mouseenter', showTooltip)
					el.removeEventListener('mouseleave', hideTooltip)
				}

				// Remove the tooltip element from the document
				if (tooltipEl.isConnected) {
					tooltipEl.remove()
				} else if (document.body?.contains(tooltipEl)) {
					document.body?.removeChild(tooltipEl)
				}
			}
		},

		// Call the cleanup function when the element is unmounted
		beforeUnmount(el: TargetElement) {
			if (el._tooltipCleanup) {
				// Call the cleanup function to remove event listeners and observers
				el?._tooltipCleanup?.()
			}
		}
	}

	// Register the tooltip directive
	nuxtApp.vueApp.directive('tooltip', vTooltip)
})
